// src/components/SurveyDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import firebase from '../db/firebase/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ArrowLeft, X, Download } from 'lucide-react';
import jsPDF from 'jspdf';

const SurveyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState('');
  const [refUser, setRefUser] = useState(null);
  const [showInstallerModal, setShowInstallerModal] = useState(false);
  const [installerName, setInstallerName] = useState('');
  const [installerEmail, setInstallerEmail] = useState('');
  const [editingInstaller, setEditingInstaller] = useState(false);
  const [editedInstallerName, setEditedInstallerName] = useState('');
  const [editedInstallerEmail, setEditedInstallerEmail] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const docRef = doc(firebase.db, 'remoteSurveys', id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          console.error('No such document!');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        setSurvey({ id: docSnap.id, ...data });

        if (data.referralCode) {
          const referralCode = data.referralCode.trim().toLowerCase();
          const usersRef = collection(firebase.db, 'userData');
          const q = query(usersRef, where('code', '==', referralCode));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const refDoc = querySnapshot.docs[0].data();
            setRefUser(refDoc);
          } else {
            setRefUser(null);
          }
        }
      } catch (error) {
        console.error('Error fetching survey or referral user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [id]);

  const toggleState = async () => {
    if (!survey) return;
    const newState = survey.state === 'pending' ? 'answer' : 'pending';
    setUpdating(true);
    try {
      const docRef = doc(firebase.db, 'remoteSurveys', survey.id);
      await updateDoc(docRef, { state: newState });
      setSurvey(prev => ({ ...prev, state: newState }));
    } catch (error) {
      console.error('Error updating state:', error);
    } finally {
      setUpdating(false);
    }
  };

  const getBase64Image = async (url) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`Error fetching image from ${url}`, error);
      return null;
    }
  };

  const handleDownloadPDF = async () => {
    if (!survey) return;

    setGeneratingPdf(true);
    try {
      const pdf = new jsPDF();
      let y = 10;

      const addText = (label, value) => {
        pdf.setFontSize(12);
        pdf.text(`${label}: ${value || 'N/A'}`, 10, y);
        y += 8;
      };

      pdf.setFontSize(16);
      pdf.text('Survey Detail', 10, y);
      y += 10;

      // Datos del cliente (incluye nuevos campos)
      addText('Full Name', survey.fullName || survey.name);
      addText('Email', survey.email);
      addText('Phone', survey.phone);
      addText('Address', survey.address);
      addText('MPAN', survey.mpan);
      addText('Cable', survey.cable);
      addText('Distance Board', survey.distanceBoard);
      addText('Distance Fuse', survey.distanceFuse);
      addText('Color Model', survey.colorModel);
      addText('Additional Info', survey.informationAdditional);
      addText('Referral Code', survey.referralCode);

      const submittedAt = survey.createdAt?.toDate
        ? survey.createdAt.toDate().toLocaleString()
        : survey.createdAt;
      addText('Submitted At', submittedAt);

      if (survey.preferredDate) {
        const dateStr = new Date(survey.preferredDate).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        addText('Preferred Installation Date', dateStr);
      }

      // Installer (si existe)
      if (survey.installerName || survey.installerEmail) {
        y += 6;
        pdf.setFontSize(14);
        pdf.text('Installer Assigned', 10, y);
        y += 8;
        addText('Installer Name', survey.installerName);
        addText('Installer Email', survey.installerEmail);
      }

      y += 10;
      pdf.setFontSize(14);
      pdf.text('Images:', 10, y);
      y += 5;

      const imageLabels = {
        fuse: 'Mains Service Fuse',
        board: 'Main Fuse Board',
        location: 'Charger Location',
        floorPlan: 'Basic Floor Plan',
      };

      let x = 10;

      for (const key of ['fuse', 'board', 'location', 'floorPlan']) {
        if (survey.images?.[key]) {
          const base64 = await getBase64Image(survey.images[key]);
          if (!base64) continue;

          if (x + 40 > 190) {
            x = 10;
            y += 60;
          }

          pdf.addImage(base64, 'PNG', x, y, 40, 40);
          pdf.setFontSize(10);
          pdf.text(imageLabels[key], x, y + 45);
          x += 50;
        }
      }

      pdf.save(`survey-${survey.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading || updating || generatingPdf) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600">
            {loading
              ? 'Loading...'
              : generatingPdf
              ? 'Generating PDF...'
              : 'Saving...'}
          </p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <p className="text-lg text-red-500">Survey not found.</p>
      </div>
    );
  }

  const { images = {} } = survey;
  const submittedAt = survey.createdAt?.toDate
    ? survey.createdAt.toDate().toLocaleString()
    : survey.createdAt;

  return (
    <div className="flex flex-1 flex-col h-screen overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white shadow">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:underline"
        >
          <ArrowLeft size={20} className="mr-2" /> Back
        </button>

        <div className="flex items-center space-x-4">
          {/* Steps (mantengo tu lógica de 3 pasos) */}
          <div className="flex items-center space-x-4">
            {[
              { label: '1- Received', value: 'Quote Request Received', index: 1 },
              { label: '2- In Generation', value: 'Quote In Generation', index: 2 },
              { label: '3- Complete', value: 'Quote Complete', index: 3 },
            ].map((step) => {
              const stateOrder = {
                pending: 0,
                'Quote Request Received': 1,
                'Quote In Generation': 2,
                'Quote Complete': 3,
              };
              const currentLevel = stateOrder[survey.state] ?? 0;
              const isActive = step.index <= currentLevel;

              const handleClick = async () => {
                if (!survey?.id || updating) return;

                let newState = step.value;

                // Si el paso ya está activo y clickeás de nuevo, retrocede uno
                if (isActive && step.index === currentLevel) {
                  if (step.index === 3) newState = 'Quote In Generation';
                  else if (step.index === 2) newState = 'Quote Request Received';
                  else if (step.index === 1) newState = 'pending';
                }

                if (newState === 'Quote In Generation') {
                  setShowInstallerModal(true);
                  return;
                }

                setUpdating(true);
                try {
                  const docRef = doc(firebase.db, 'remoteSurveys', survey.id);
                  await updateDoc(docRef, { state: newState });
                  setSurvey(prev => ({ ...prev, state: newState }));
                } catch (err) {
                  console.error('Error updating state:', err);
                } finally {
                  setUpdating(false);
                }
              };

              return (
                <div
                  key={step.value}
                  className="flex items-center space-x-2 bg-gray-300 px-4 py-2 rounded-full"
                >
                  <span className="text-sm font-medium">{step.label}</span>
                  <button
                    onClick={handleClick}
                    disabled={updating}
                    className={`
                      w-10 h-6 rounded-full relative transition-colors duration-300
                      ${isActive ? 'bg-green-500' : 'bg-gray-500'}
                      ${updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <span
                      className={`
                        absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300
                        ${isActive ? 'translate-x-4' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Botón PDF */}
          <button
            onClick={handleDownloadPDF}
            className="flex items-center px-3 py-2 border rounded bg-green-600 text-white hover:bg-green-700"
          >
            <Download size={18} className="mr-2" />
            PDF
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setLightboxUrl('')}
        >
          <button
            className="absolute top-4 right-4 text-white"
            onClick={() => setLightboxUrl('')}
          >
            <X size={32} />
          </button>
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-h-full max-w-full rounded"
          />
        </div>
      )}

      {/* Content */}
      <div id="pdfContent" className="flex-1 w-full mx-auto p-6 space-y-6">
        <h1 className="text-3xl font-bold">Survey Detail</h1>

        <div className="space-y-6 bg-white p-6 rounded shadow">
          {(survey.state === 'Quote In Generation' || survey.state === 'Quote Complete') && (
            <div className="mt-4 ml-1 space-y-2 text-sm text-gray-700 border p-4 rounded bg-white">
              <h3 className="text-lg font-semibold">Installer Assigned</h3>

              {editingInstaller ? (
                <>
                  <div>
                    <label className="block text-xs font-medium">Installer Name</label>
                    <input
                      type="text"
                      value={editedInstallerName}
                      onChange={(e) => setEditedInstallerName(e.target.value)}
                      className="border w-full p-2 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium">Installer Email</label>
                    <input
                      type="email"
                      value={editedInstallerEmail}
                      onChange={(e) => setEditedInstallerEmail(e.target.value)}
                      className="border w-full p-2 rounded mt-1"
                    />
                  </div>

                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={async () => {
                        if (!editedInstallerName || !editedInstallerEmail) {
                          alert('Please fill in both fields.');
                          return;
                        }
                        setUpdating(true);
                        try {
                          const docRef = doc(firebase.db, 'remoteSurveys', survey.id);
                          await updateDoc(docRef, {
                            installerName: editedInstallerName,
                            installerEmail: editedInstallerEmail,
                          });
                          setSurvey(prev => ({
                            ...prev,
                            installerName: editedInstallerName,
                            installerEmail: editedInstallerEmail,
                          }));
                          setEditingInstaller(false);
                        } catch (error) {
                          console.error('Error updating installer info:', error);
                        } finally {
                          setUpdating(false);
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingInstaller(false)}
                      className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Name:</strong> {survey.installerName || 'N/A'}</p>
                  <p><strong>Email:</strong> {survey.installerEmail || 'N/A'}</p>
                  <button
                    onClick={() => {
                      setEditedInstallerName(survey.installerName || '');
                      setEditedInstallerEmail(survey.installerEmail || '');
                      setEditingInstaller(true);
                    }}
                    className="mt-2 px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    Edit Installer
                  </button>
                </>
              )}
            </div>
          )}

          {/* Nuevos campos + existentes */}
          <div>
            <h2 className="text-xl font-semibold">Full Name</h2>
            <p>{survey.fullName || survey.name || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold">Email</h2>
            <p>{survey.email}</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold">Phone Number</h2>
            <p>{survey.phone || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold">Address</h2>
            <p>{survey.address}</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold">MPAN</h2>
            <p>{survey.mpan}</p>
          </div>

          {/* Imágenes con preview */}
          <div>
            <h2 className="text-xl font-semibold mb-2">Images</h2>
            <div className="flex flex-row gap-6 overflow-x-auto pb-2">
              {[
                { key: 'fuse', label: 'Mains Service Fuse' },
                { key: 'board', label: 'Main Fuse Board' },
                { key: 'location', label: 'Charger Location' },
                { key: 'floorPlan', label: 'Basic Floor Plan' },
              ].map(({ key, label }) => (
                <div key={key} className="flex-shrink-0 w-48">
                  {images[key] ? (
                    <img
                      src={images[key]}
                      alt={label}
                      className="rounded w-full h-48 object-cover cursor-pointer"
                      onClick={() => setLightboxUrl(images[key])}
                    />
                  ) : (
                    <p className="text-gray-400">No image</p>
                  )}
                  <p className="text-sm font-medium text-gray-600 my-1">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-8">
            <div>
              <h2 className="text-xl font-semibold">Charger with Cable?</h2>
              <p>{survey.cable}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-8">
            <div>
              <h2 className="text-xl font-semibold">
                Distance: Board to Charger Point
              </h2>
              <p>{survey.distanceBoard}</p>
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                Distance: Fuse to Charger Point
              </h2>
              <p>{survey.distanceFuse}</p>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold">Preferred Color Model</h2>
            <p>{survey.colorModel}</p>
          </div>
          <div>
            <h2 className="text-xl font-semibold">Additional Information</h2>
            <p>{survey.informationAdditional}</p>
          </div>

          {survey.preferredDate && (
            <div>
              <h2 className="text-xl font-semibold">Preferred Installation Date</h2>
              <p>
                {new Date(survey.preferredDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          )}

          {survey && (
            <div>
              <h2 className="text-xl font-semibold">Referral</h2>
              {refUser ? (
                <div>
                  <p className="text-gray-700">👤 {refUser.name || 'Unnamed User'}</p>
                  <p className="text-gray-500">📧 {refUser.email}</p>
                </div>
              ) : (
                <p>{`Code: ${survey.referralCode}`}</p>
              )}
            </div>
          )}

          <div>
            <h2 className="text-xl font-semibold">Submitted At</h2>
            <p>{submittedAt}</p>
          </div>
        </div>
      </div>

      {/* Modal Asignar Instalador */}
      {showInstallerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[90%] max-w-md space-y-4">
            <h2 className="text-xl font-semibold mb-4">Assign Installer</h2>
            <div>
              <label className="block text-sm font-medium">Installer Name</label>
              <input
                type="text"
                className="w-full border p-2 rounded mt-1"
                value={installerName}
                onChange={(e) => setInstallerName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Installer Email</label>
              <input
                type="email"
                className="w-full border p-2 rounded mt-1"
                value={installerEmail}
                onChange={(e) => setInstallerEmail(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowInstallerModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!installerName || !installerEmail) return alert('Please fill in all fields');
                  setUpdating(true);
                  try {
                    const docRef = doc(firebase.db, 'remoteSurveys', survey.id);
                    await updateDoc(docRef, {
                      state: 'Quote In Generation',
                      installerName,
                      installerEmail,
                    });
                    setSurvey(prev => ({
                      ...prev,
                      state: 'Quote In Generation',
                      installerName,
                      installerEmail,
                    }));
                    setShowInstallerModal(false);
                  } catch (err) {
                    console.error('Error saving installer:', err);
                    alert('Error saving installer');
                  } finally {
                    setUpdating(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyDetail;